import React, { useState, useEffect, useCallback } from 'react';
import { Settings2, MessageSquare, Save, Store, Star, RefreshCw, X } from 'lucide-react';
import { API_BASE_URL } from '../../config';

const DEFAULT_BOT = {
    bot_welcome_text: '',
    bot_help_answer: '',
    msg_payment_success: '✅ **Звезды успешно зачислены!**\n\n📋 **ID сделки:** `{order_id}`\n\nСохраните этот ID для быстрого решения вопросов с поддержкой.',
    msg_user_blocked: '⛔ Ваш пользователь забанен. По вопросам обращайтесь в поддержку.',
    msg_recipient_blocked: '❌ Получатель заблокирован антифрод-системой. Выберите другого получателя или обратитесь в поддержку.',
    msg_antifraud_user_limit: '❌ По антифрод-правилам для одного пользователя доступно не более 3 успешных отправок дороже 500 ₽.',
    msg_antifraud_recipient_limit: '❌ По антифрод-правилам одному получателю доступно не более 3 успешных отправок дороже 500 ₽.',
    bot_btn_buy: 'Купить ',
    bot_btn_digital_goods: '🛍 Цифровые товары',
    bot_btn_help: 'Помощь ',
    bot_btn_links: 'Ссылки ',
    bot_btn_menu: '📋 Меню',
    btn_buy_self: 'Купить себе',
    btn_buy_friend: 'Купить другу',
    min_stars: '50',
    autofunnel_register_msg_1: 'Наш сервис имеет более 1000 положительных отзывов, смотри тут\nhttps://t.me/otzyvstars9',
    autofunnel_register_msg_2: 'Наш сервис имеет цену звезды 1,33 рубля, что на 35 % дешевле официального бота телеграмм. Почему такие скидки? Официальный бот телеграмм продает звезды через GooglePay и ApplePay, которые берут комиссию с телегамма в размере 30-35%. Мы же использует более дешевые платежные системы не менее качественные',
    autofunnel_register_msg_3: 'Если у тебя есть какой-либо вопрос, напиши нам @suppurtstarspay. Мы с радостью ответим на любой вопрос',
    autofunnel_unpaid_order_msg: 'Уважаемый, {name}! Вы создали счет, но не оплатили его! Если возникли трудности или есть вопросы, напишите нам @suppurtstarspay',
    autofunnel_first_paid_msg: 'Поздравляем с первой покупкой! Если хочешь собственного такого телерамм бота, через которого можно покупать телеграмм звезды, тут можешь создать своего бота\nhttps://t.me/Starspay9bot?start=bots',
    autofunnel_referral_menu_msg: 'Вы так и не создали ни одного собственного бота, если возникли трудности или вопросы, напишите нам @suppurtstarspay мы с удовольствием поможем Вам',
    autofunnel_register_delay_1_sec: '60',
    autofunnel_register_delay_2_sec: '300',
    autofunnel_register_delay_3_sec: '300',
    autofunnel_unpaid_order_delay_sec: '180',
    autofunnel_first_paid_delay_sec: '300',
    autofunnel_referral_menu_delay_sec: '30',
    digital_goods_visible: '0',
    ordinary_gift_send_source: 'operator',
    premium_send_source: 'operator',
    operator_tonkeeper_auto_fund_enabled: '0',
    operator_tonkeeper_auto_fund_amount: '50',
};

const userLabel = (user) => {
    const username = user?.username ? `@${String(user.username).replace(/^@/, '')}` : null;
    const fio = (user?.fio || '').trim() || null;
    const tg = user?.tg_id != null ? `tg:${user.tg_id}` : null;
    return [username, fio, tg, `#${user?.id}`].filter(Boolean).join(' · ');
};

const BotSettingsPage = ({ onShowToast }) => {
    const [botSettings, setBotSettings] = useState(DEFAULT_BOT);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [balanceStars, setBalanceStars] = useState(null);
    const [balanceOk, setBalanceOk] = useState(false);
    const [balanceError, setBalanceError] = useState('');
    const [balanceLoading, setBalanceLoading] = useState(false);
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [userQuery, setUserQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearching, setUserSearching] = useState(false);

    const applyTopupPayload = useCallback((data) => {
        setBalanceStars(data.bot_stars_balance ?? null);
        setBalanceOk(Boolean(data.bot_stars_balance_ok));
        setBalanceError(data.bot_stars_balance_error || '');
        const users = Array.isArray(data.bot_stars_topup_allowed_users)
            ? data.bot_stars_topup_allowed_users
            : [];
        setAllowedUsers(users);
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const r = await fetch(`${API_BASE_URL}/api/settings/bot`, {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (!r.ok) throw new Error('Failed to fetch');
                const data = await r.json();
                setBotSettings({ ...DEFAULT_BOT, ...data });
                applyTopupPayload(data);
            } catch (e) {
                console.error('Error loading bot settings:', e);
                if (onShowToast) onShowToast('Не удалось загрузить настройки');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [onShowToast, applyTopupPayload]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBotSettings((prev) => ({ ...prev, [name]: value }));
    };

    const digitalGoodsVisible = ['1', 'true', 'yes', 'on'].includes(
        String(botSettings.digital_goods_visible ?? '').trim().toLowerCase()
    );
    const operatorAutoFundEnabled = ['1', 'true', 'yes', 'on'].includes(
        String(botSettings.operator_tonkeeper_auto_fund_enabled ?? '').trim().toLowerCase()
    );
    const ordinaryGiftSource =
        String(botSettings.ordinary_gift_send_source || 'operator').trim().toLowerCase() === 'bot'
            ? 'bot'
            : 'operator';
    const premiumSendSource =
        String(botSettings.premium_send_source || 'operator').trim().toLowerCase() === 'bot'
            ? 'bot'
            : 'operator';

    const toggleDigitalGoodsVisible = () => {
        setBotSettings((prev) => ({
            ...prev,
            digital_goods_visible: digitalGoodsVisible ? '0' : '1',
        }));
    };

    const toggleOperatorAutoFund = () => {
        setBotSettings((prev) => ({
            ...prev,
            operator_tonkeeper_auto_fund_enabled: operatorAutoFundEnabled ? '0' : '1',
        }));
    };

    const refreshBalance = async () => {
        try {
            setBalanceLoading(true);
            const r = await fetch(`${API_BASE_URL}/api/settings/bot/stars-balance`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const body = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(body.detail || 'Не удалось получить баланс');
            setBalanceOk(Boolean(body.ok));
            setBalanceStars(body.balance_stars ?? null);
            setBalanceError(body.error || '');
        } catch (e) {
            setBalanceOk(false);
            setBalanceError(e.message || 'Ошибка баланса');
            if (onShowToast) onShowToast(e.message || 'Не удалось обновить баланс');
        } finally {
            setBalanceLoading(false);
        }
    };

    const searchUsers = async () => {
        const q = userQuery.trim();
        if (!q) {
            setUserSearchResults([]);
            return;
        }
        try {
            setUserSearching(true);
            const params = new URLSearchParams({ q, limit: '20' });
            const response = await fetch(`${API_BASE_URL}/api/users/clients?${params.toString()}`, {
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });
            const body = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(body.detail || 'Не удалось найти пользователей');
            setUserSearchResults(Array.isArray(body.items) ? body.items : []);
        } catch (searchError) {
            setUserSearchResults([]);
            if (onShowToast) onShowToast(searchError.message || 'Не удалось найти пользователей');
        } finally {
            setUserSearching(false);
        }
    };

    const addAllowedUser = (user) => {
        if (!user?.id) return;
        setAllowedUsers((current) => {
            if (current.some((item) => item.id === user.id)) return current;
            return [
                ...current,
                {
                    id: user.id,
                    username: user.username,
                    fio: user.fio,
                    email: user.email,
                    tg_id: user.tg_id,
                    missing: false,
                },
            ];
        });
        setUserSearchResults((current) => current.filter((item) => item.id !== user.id));
    };

    const removeAllowedUser = (userId) => {
        setAllowedUsers((current) => current.filter((user) => user.id !== userId));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const payload = {
                ...botSettings,
                bot_stars_topup_allowed_user_ids: allowedUsers.map((user) => user.id),
            };
            delete payload.bot_stars_topup_allowed_users;
            delete payload.bot_stars_topup_allowed_user_ids_list;
            delete payload.bot_stars_balance;
            delete payload.bot_stars_balance_ok;
            delete payload.bot_stars_balance_error;

            const r = await fetch(`${API_BASE_URL}/api/settings/bot`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!r.ok) {
                const err = await r.json().catch(() => ({}));
                throw new Error(err.detail || 'Failed to save');
            }
            const data = await r.json().catch(() => ({}));
            setBotSettings((prev) => ({ ...prev, ...data }));
            applyTopupPayload(data);
            if (onShowToast) onShowToast('Настройки бота сохранены');
            window.dispatchEvent(new CustomEvent('admin-bot-settings-saved'));
        } catch (e) {
            console.error('Error saving bot settings:', e);
            if (onShowToast) onShowToast(e.message || 'Ошибка сохранения');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-2xl space-y-6 pb-10">
                <h2 className="text-2xl font-bold text-slate-800">Настройки бота</h2>
                <div className="flex items-center justify-center py-12 text-slate-500">Загрузка...</div>
            </div>
        );
    }

    return (
        <div className="max-w-2xl space-y-6 pb-10">
            <h2 className="text-2xl font-bold text-slate-800">Настройки бота</h2>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-violet-100 text-violet-700 rounded-lg">
                        <Star size={22} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Подарки и Premium</h3>
                        <p className="text-sm text-slate-500">
                            Откуда слать обычные подарки и Telegram Premium из рулетки, плюс автопополнение Stars оператора через Tonkeeper.
                        </p>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Отправлять обычные подарки
                    </label>
                    <select
                        name="ordinary_gift_send_source"
                        value={ordinaryGiftSource}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg text-sm"
                    >
                        <option value="operator">С аккаунта оператора (по умолчанию)</option>
                        <option value="bot">С бота (`sendGift` + баланс бота)</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                        Оператор: session из «Автовыдача подарков», получателю нужен @username.
                        Бот: как раньше через Bot API.
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Отправлять Telegram Premium
                    </label>
                    <select
                        name="premium_send_source"
                        value={premiumSendSource}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-lg text-sm"
                    >
                        <option value="operator">С аккаунта оператора (по умолчанию)</option>
                        <option value="bot">С бота (`giftPremiumSubscription` + баланс бота)</option>
                    </select>
                    <p className="mt-1 text-xs text-slate-500">
                        Оператор: Stars с session оператора, получателю нужен @username.
                        Бот: Bot API, Stars с баланса бота.
                    </p>
                </div>

                <label className="flex items-start gap-3 text-sm font-medium text-slate-700">
                    <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 accent-indigo-600"
                        checked={operatorAutoFundEnabled}
                        onChange={toggleOperatorAutoFund}
                    />
                    <span>
                        Автопополнение Stars оператора через Tonkeeper
                        <span className="mt-1 block text-xs font-normal text-slate-500">
                            По умолчанию выключено. При нехватке баланса на обычных/коллекционных подарках и Premium —
                            Tonkeeper отправит Stars на @username оператора, выдача повторится.
                        </span>
                    </span>
                </label>

                <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                        Сумма автопополнения, Stars
                    </label>
                    <input
                        name="operator_tonkeeper_auto_fund_amount"
                        type="number"
                        min="50"
                        value={botSettings.operator_tonkeeper_auto_fund_amount ?? '50'}
                        onChange={handleInputChange}
                        disabled={!operatorAutoFundEnabled}
                        className="w-full p-2 border rounded-lg text-sm disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <p className="mt-1 text-xs text-slate-500">Минимум 50 (лимит Tonkeeper). Фактически отправится max(сумма, нехватка).</p>
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
                        <Star size={22} />
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-slate-800">Баланс бота, Stars</h3>
                        <p className="text-sm text-slate-500">
                            Нужен, если обычные подарки или Premium идут с бота. Кто в списке — видит в боте «Пополнить баланс бота».
                        </p>
                    </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Текущий баланс бота</div>
                        <div className="mt-1 text-2xl font-bold text-slate-900">
                            {balanceOk && balanceStars != null ? `${Number(balanceStars).toLocaleString('ru-RU')} Stars` : '—'}
                        </div>
                        {!balanceOk && balanceError ? (
                            <div className="mt-1 text-sm text-rose-600">{balanceError}</div>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        onClick={() => void refreshBalance()}
                        disabled={balanceLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                    >
                        <RefreshCw size={16} className={balanceLoading ? 'animate-spin' : ''} />
                        {balanceLoading ? 'Обновление…' : 'Обновить'}
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Кому показывать пополнение в боте
                        </label>
                        <p className="text-sm text-slate-500 mb-2">
                            Поиск по username. Пустой список — пункт никому не виден.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                            value={userQuery}
                            onChange={(event) => setUserQuery(event.target.value)}
                            placeholder="Поиск: @username, id, email, tg_id"
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    void searchUsers();
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="inline-flex h-10 items-center justify-center rounded-lg bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-60"
                            disabled={userSearching || !userQuery.trim()}
                            onClick={() => void searchUsers()}
                        >
                            {userSearching ? 'Ищем…' : 'Найти'}
                        </button>
                    </div>
                    {userSearchResults.length > 0 && (
                        <div className="overflow-hidden rounded-lg border border-slate-200">
                            {userSearchResults.map((user) => {
                                const selected = allowedUsers.some((item) => item.id === user.id);
                                return (
                                    <button
                                        key={user.id}
                                        type="button"
                                        disabled={selected}
                                        onClick={() => addAllowedUser(user)}
                                        className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 disabled:cursor-default disabled:bg-slate-50 disabled:text-slate-400"
                                    >
                                        <span>{userLabel(user)}</span>
                                        <span className="text-xs font-semibold uppercase tracking-wide">
                                            {selected ? 'Добавлен' : 'Добавить'}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {allowedUsers.length ? (
                        <div className="flex flex-wrap gap-2">
                            {allowedUsers.map((user) => (
                                <span
                                    key={user.id}
                                    className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-100 bg-amber-50 px-3 py-1.5 text-sm text-amber-950"
                                >
                                    <span className="truncate">{userLabel(user)}</span>
                                    <button
                                        type="button"
                                        aria-label={`Убрать ${user.id}`}
                                        className="rounded-full p-0.5 text-amber-800 hover:bg-amber-100"
                                        onClick={() => removeAllowedUser(user.id)}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-slate-500">Никто не выбран — кнопка пополнения скрыта.</div>
                    )}
                </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 space-y-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                        <Settings2 size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800">Конфигурация интерфейса</h3>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <MessageSquare size={16} className="text-slate-400" /> Приветствие
                        </label>
                        <textarea
                            name="bot_welcome_text"
                            value={botSettings.bot_welcome_text ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[100px] text-sm"
                            placeholder="Текст при старте бота..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Ответ поддержки</label>
                        <textarea
                            name="bot_help_answer"
                            value={botSettings.bot_help_answer ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[100px] text-sm"
                            placeholder="Текст для раздела помощи..."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Финальное сообщение об успешной оплате</label>
                        <textarea
                            name="msg_payment_success"
                            value={botSettings.msg_payment_success ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[120px] text-sm"
                            placeholder="Можно использовать плейсхолдер {order_id}"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Сообщение забаненному пользователю</label>
                        <textarea
                            name="msg_user_blocked"
                            value={botSettings.msg_user_blocked ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[90px] text-sm"
                            placeholder="Текст при попытке зайти в бот забаненному пользователю"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Сообщение при заблокированном получателе</label>
                        <textarea
                            name="msg_recipient_blocked"
                            value={botSettings.msg_recipient_blocked ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[90px] text-sm"
                            placeholder="Текст при попытке отправить Stars заблокированному получателю"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Сообщение при лимите пользователя</label>
                        <textarea
                            name="msg_antifraud_user_limit"
                            value={botSettings.msg_antifraud_user_limit ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[90px] text-sm"
                            placeholder="Текст, когда пользователь превысил лимит дорогих отправок"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Сообщение при лимите получателя</label>
                        <textarea
                            name="msg_antifraud_recipient_limit"
                            value={botSettings.msg_antifraud_recipient_limit ?? ''}
                            onChange={handleInputChange}
                            className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none min-h-[90px] text-sm"
                            placeholder="Текст, когда получатель превысил лимит дорогих отправок"
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Купить»</label>
                            <input name="bot_btn_buy" value={botSettings.bot_btn_buy ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Помощь»</label>
                            <input name="bot_btn_help" value={botSettings.bot_btn_help ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Ссылки»</label>
                            <input name="bot_btn_links" value={botSettings.bot_btn_links ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Меню»</label>
                            <input name="bot_btn_menu" value={botSettings.bot_btn_menu ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Купить себе»</label>
                            <input name="btn_buy_self" value={botSettings.btn_buy_self ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Кнопка «Купить другу»</label>
                            <input name="btn_buy_friend" value={botSettings.btn_buy_friend ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-slate-500 uppercase">Минимум Stars</label>
                            <input name="min_stars" type="number" value={botSettings.min_stars ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                        </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                                <div className="rounded-lg bg-indigo-100 p-2 text-indigo-600">
                                    <Store size={18} />
                                </div>
                                <div>
                                    <div className="font-medium text-slate-800">Раздел «Цифровые товары»</div>
                                    <div className="text-sm text-slate-500">
                                        Управляет кнопкой в стартовом меню бота.
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={toggleDigitalGoodsVisible}
                                className={`min-w-[132px] rounded-lg px-4 py-2 text-sm font-medium transition ${
                                    digitalGoodsVisible
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-100'
                                }`}
                            >
                                {digitalGoodsVisible ? 'Показывать' : 'Скрыто'}
                            </button>
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <h4 className="font-semibold text-slate-800 mb-3">Автоворонка: тексты и тайминги</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Регистрация: сообщение 1</label>
                                <textarea name="autofunnel_register_msg_1" value={botSettings.autofunnel_register_msg_1 ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Задержка 1 (сек)</label>
                                <input name="autofunnel_register_delay_1_sec" value={botSettings.autofunnel_register_delay_1_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Регистрация: сообщение 2</label>
                                <textarea name="autofunnel_register_msg_2" value={botSettings.autofunnel_register_msg_2 ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Задержка 2 (сек)</label>
                                <input name="autofunnel_register_delay_2_sec" value={botSettings.autofunnel_register_delay_2_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Регистрация: сообщение 3</label>
                                <textarea name="autofunnel_register_msg_3" value={botSettings.autofunnel_register_msg_3 ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Задержка 3 (сек)</label>
                                <input name="autofunnel_register_delay_3_sec" value={botSettings.autofunnel_register_delay_3_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Неоплаченный счет: текст</label>
                                <textarea name="autofunnel_unpaid_order_msg" value={botSettings.autofunnel_unpaid_order_msg ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Неоплаченный счет: задержка (сек)</label>
                                <input name="autofunnel_unpaid_order_delay_sec" value={botSettings.autofunnel_unpaid_order_delay_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Первая оплата: текст</label>
                                <textarea name="autofunnel_first_paid_msg" value={botSettings.autofunnel_first_paid_msg ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Первая оплата: задержка (сек)</label>
                                <input name="autofunnel_first_paid_delay_sec" value={botSettings.autofunnel_first_paid_delay_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Реферальная система: текст</label>
                                <textarea name="autofunnel_referral_menu_msg" value={botSettings.autofunnel_referral_menu_msg ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm min-h-[90px]" />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-500 uppercase">Реферальная система: задержка (сек)</label>
                                <input name="autofunnel_referral_menu_delay_sec" value={botSettings.autofunnel_referral_menu_delay_sec ?? ''} onChange={handleInputChange} className="w-full p-2 border rounded-lg text-sm" />
                            </div>
                        </div>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-indigo-600 text-white py-3 rounded-lg font-medium hover:bg-indigo-700 transition flex justify-center items-center gap-2 disabled:opacity-50"
                >
                    <Save size={18} /> {saving ? 'Сохранение…' : 'Сохранить настройки бота'}
                </button>
            </div>
        </div>
    );
};

export default BotSettingsPage;
